import type { FormEvent } from "react";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import PasswordField from "@/components/forms/PasswordField";

type SharedPasswordPromptProps = {
  password: string;
  passwordError: boolean;
  submitting: boolean;
  title: string;
  description: string;
  placeholder: string;
  errorMessage: string;
  submitLabel: string;
  submittingLabel: string;
  onChangePassword: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
};

export default function SharedPasswordPrompt({
  password,
  passwordError,
  submitting,
  title,
  description,
  placeholder,
  errorMessage,
  submitLabel,
  submittingLabel,
  onChangePassword,
  onSubmit,
}: SharedPasswordPromptProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4 safe-x safe-bottom">
      <Card className="w-full max-w-sm shadow-lg">
        <CardContent className="p-8">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400">
              <Lock className="h-6 w-6" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-bold text-foreground">{title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <FormField
              id="shared-password"
              label={title}
              error={passwordError ? errorMessage : undefined}
              className="[&>label]:sr-only"
            >
              <PasswordField
                id="shared-password"
                value={password}
                onChange={(e) => onChangePassword(e.target.value)}
                placeholder={placeholder}
                autoFocus
                invalid={passwordError}
                autoComplete="current-password"
              />
            </FormField>
            <Button
              type="submit"
              variant="gradient"
              size="lg"
              className="w-full"
              disabled={!password.trim()}
              loading={submitting}
            >
              {submitting ? submittingLabel : submitLabel}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
